import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPremium: boolean;
  planType: string;
  refreshSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isPremium: false,
  planType: 'free',
  refreshSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [planType, setPlanType] = useState('free');
  const checkedUserRef = useRef<string | null>(null);

  const checkSubscription = useCallback(async (userId: string, force = false) => {
    if (!force && checkedUserRef.current === userId) return;
    checkedUserRef.current = userId;
    const { data } = await supabase
      .from('subscriptions')
      .select('plan, status, expires_at')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();
    const premium = !!data && data.plan !== 'free' && (!data.expires_at || new Date(data.expires_at) > new Date());
    setIsPremium(premium);
    setPlanType(premium && data ? data.plan : 'free'); // 'pro' | 'ultra' | 'lifetime' | 'free'
  }, []);

  const refreshSubscription = useCallback(async () => {
    if (user) {
      await checkSubscription(user.id, true);
    }
  }, [user, checkSubscription]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        checkSubscription(session.user.id);
      } else {
        checkedUserRef.current = null;
        setIsPremium(false);
        setPlanType('free');
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        checkSubscription(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  // Re-check subscription when tab becomes visible or window regains focus
  // (covers Dodo checkout closing without redirect to /payment-success)
  useEffect(() => {
    if (!user) return;
    const recheck = () => {
      if (document.visibilityState === 'visible') {
        checkSubscription(user.id, true);
      }
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [user, checkSubscription]);

  // Realtime: react to subscription row changes for this user
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subs-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'subscriptions', filter: `user_id=eq.${user.id}` },
        () => { checkSubscription(user.id, true); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isPremium, planType, refreshSubscription, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
